/**
 * lvMask.js — полость левого желудочка и фракция выброса по коротко-осевому кино.
 *
 * ЗАЧЕМ. ФВ считают по объёмам полости в конце диастолы и в конце систолы:
 * ФВ = (КДО − КСО)/КДО. Объём набирается методом дисков (Симпсона): площадь
 * полости на каждом срезе умножается на расстояние между срезами и суммируется.
 * Вся трудность не в этой арифметике, а в том, ЧТО считать полостью.
 *
 * КАК ИЩЕТСЯ ПОЛОСТЬ. Полость в кино-последовательности яркая (кровь), миокард
 * темнее. Порог берётся не константой — константа в МР бессмысленна, яркость не
 * калибрована, — а от самой картинки: по гистограмме внутри области интереса
 * ищется разделение кровь/миокард (Оцу), затем от зерна внутри полости растёт
 * область по связности (заливка), и берётся только та компонента, что содержит
 * зерно. Связность важна: порог сам по себе прихватывает правый желудочек,
 * подкожный жир и лёгочные сосуды, а заливка от зерна их не берёт, если между
 * ними есть стенка.
 *
 * ЧЕГО ЗДЕСЬ НЕТ, и это надо знать до того, как смотреть на число:
 *
 *   · ЗЕРНО ЗАДАЁТ ЧЕЛОВЕК (или центр области интереса). Автоматического поиска
 *     желудочка здесь нет. Ставить зерно алгоритмом «самое яркое пятно» —
 *     значит иногда считать ФВ по правому желудочку и молчать об этом.
 *   · ПАПИЛЛЯРНЫЕ МЫШЦЫ. Они темнее крови и лежат ВНУТРИ полости. Заливка их
 *     исключит, и объём выйдет меньше — а принято включать их в полость.
 *     Поэтому есть отдельный шаг: дырки внутри полости заполняются, и число
 *     заполненных пикселей возвращается наружу (`папиллярных`). Оба ответа
 *     доступны: с мышцами в полости и без. Разница на живых данных — единицы
 *     процентов ФВ, и молчать о ней нельзя.
 *   · БАЗАЛЬНЫЙ СРЕЗ И ВЫХОДНОЙ ОТДЕЛ. Где кончается желудочек и начинается
 *     предсердие, решает человек по клапану. Здесь этого решения нет: считаются
 *     те срезы, которые дали. Ошибка в один базальный срез — это несколько
 *     миллилитров и несколько процентов ФВ.
 *   · Нет коррекции движения между фазами, нет учёта косого положения оси,
 *     нет полуавтоматической правки контура. Маска — не контур: её граница идёт
 *     по пикселям, и на срезе 1,5×1,5 мм это ступенька в полтора миллиметра.
 *
 * Срез, на котором разделение кровь/миокард не нашлось или область упёрлась в
 * границу кадра, НЕ получает площадь «0 мм²» — он получает отказ с причиной и
 * не участвует в объёме, а число таких срезов возвращается вызывающему. Ноль
 * квадратных миллиметров в сумме объёма — это ложь, которую по цветной картинке
 * не увидеть.
 */

/** Причины отказа по срезу — считаются и возвращаются наружу. */
export const ПРИЧИНЫ = Object.freeze({
  РАЗДЕЛЕНИЕ: 'кровь и миокард не разделяются по яркости',
  ЗЕРНО: 'зерно вне полости: яркость под порогом',
  ГРАНИЦА: 'область доросла до края кадра — это не замкнутая полость',
  МАЛО: 'область меньше разумного минимума',
  МНОГО: 'область больше разумного максимума — вероятно, вытекла за стенку',
});

const ПО_УМОЛЧАНИЮ = Object.freeze({
  минПлощадьММ2: 60,      // меньше — это не полость, а шум или сосуд
  максДоляКадра: 0.35,    // больше трети кадра — заливка вытекла
  заполнятьПапиллярные: true,
  радиусЗоныММ: 60,       // область интереса вокруг зерна: остальное не смотрим
  // Порог по безразмерному контрасту |m₁−m₀|/(σ₀+σ₁). Числа замерены:
  // две ткани 100/900 дают 138; кровь против миокарда с шумом 10% — 8,5;
  // однородный шум 500±5 — 2,06. Граница 3 лежит между ними с запасом втрое.
  минРазделимость: 3,
});

/**
 * Порог Оцу по гистограмме — с двумя поправками, каждая найдена замером.
 *
 * 1. ПОРОГ БЕРЁТСЯ ПО СЕРЕДИНЕ ПЛАТО. Когда между тканями широкий провал, целая
 *    полоса порогов даёт одну и ту же межклассовую дисперсию, и обычный поиск
 *    максимума возвращает ПЕРВЫЙ из них — то есть край тёмного класса. Замер:
 *    на двух тканях 100 и 900 такой поиск давал 109,5 вместо ~500, и любой шум
 *    сразу перетаскивал пиксели через порог. Берём середину плато.
 *
 * 2. МЕРА РАЗДЕЛИМОСТИ — НЕ доля межклассовой дисперсии. Она у Оцу высока
 *    ВСЕГДА, даже когда делить нечего: на однородном шуме 500±5 замер дал 0,81 —
 *    по такому признаку отказать невозможно. Возвращается безразмерный контраст
 *    |m₁ − m₀| / (σ₀ + σ₁): у крови с миокардом это единицы, у одной ткани —
 *    меньше двух. Числа закреплены в tests/lvmask_test.mjs.
 *
 * @returns {{порог: number, разделимость: number, среднееНиз: number, среднееВерх: number}}
 */
export function порогОцу(значения, корзин = 128) {
  let мин = Infinity, макс = -Infinity, n = 0;
  for (const v of значения) {
    if (!Number.isFinite(v)) continue;
    if (v < мин) мин = v;
    if (v > макс) макс = v;
    n++;
  }
  const пусто = { порог: NaN, разделимость: 0, среднееНиз: NaN, среднееВерх: NaN };
  if (!n || !(макс > мин)) return пусто;

  const гист = new Float64Array(корзин);
  const шаг = (макс - мин) / корзин;
  for (const v of значения) {
    if (!Number.isFinite(v)) continue;
    let k = Math.floor((v - мин) / шаг);
    if (k >= корзин) k = корзин - 1;
    if (k < 0) k = 0;
    гист[k]++;
  }

  let общаяСумма = 0;
  for (let k = 0; k < корзин; k++) общаяСумма += гист[k] * k;

  let весНиз = 0, суммаНиз = 0, лучшая = -1;
  const плато = [];
  for (let k = 0; k < корзин - 1; k++) {
    весНиз += гист[k];
    суммаНиз += гист[k] * k;
    if (весНиз === 0) continue;
    const весВерх = n - весНиз;
    if (весВерх === 0) break;
    const срНиз = суммаНиз / весНиз;
    const срВерх = (общаяСумма - суммаНиз) / весВерх;
    const между = весНиз * весВерх * (срНиз - срВерх) * (срНиз - срВерх);
    if (между > лучшая * (1 + 1e-9)) { лучшая = между; плато.length = 0; плато.push(k); }
    else if (лучшая > 0 && между >= лучшая * (1 - 1e-9)) плато.push(k);
  }
  if (!плато.length) return пусто;

  const kСередина = плато[Math.floor(плато.length / 2)];
  const порог = мин + (kСередина + 0.5) * шаг;

  // Средние и разбросы двух классов — в исходных единицах, по гистограмме.
  let n0 = 0, s0 = 0, q0 = 0, n1 = 0, s1 = 0, q1 = 0;
  for (let k = 0; k < корзин; k++) {
    const c = гист[k];
    if (!c) continue;
    const v = мин + (k + 0.5) * шаг;
    if (k <= kСередина) { n0 += c; s0 += c * v; q0 += c * v * v; }
    else { n1 += c; s1 += c * v; q1 += c * v * v; }
  }
  if (!n0 || !n1) return { порог, разделимость: 0, среднееНиз: NaN, среднееВерх: NaN };
  const m0 = s0 / n0, m1 = s1 / n1;
  const д0 = Math.max(0, q0 / n0 - m0 * m0), д1 = Math.max(0, q1 / n1 - m1 * m1);
  const разброс = Math.sqrt(д0) + Math.sqrt(д1);
  const разделимость = разброс > 1e-9 ? Math.abs(m1 - m0) / разброс : Infinity;

  return { порог, разделимость, среднееНиз: m0, среднееВерх: m1 };
}

/**
 * Заливка от зерна по связности 4 с порогом «яркость ≥ порога».
 * Возвращает маску, площадь в пикселях и признак «дошла до края кадра».
 */
export function заливкаОтЗерна(данные, ширина, высота, зерно, порог, зона = null) {
  const N = ширина * высота;
  const маска = new Uint8Array(N);
  const [sx, sy] = зерно;
  if (sx < 0 || sy < 0 || sx >= ширина || sy >= высота) {
    return { маска, пикселей: 0, доКрая: false, причина: ПРИЧИНЫ.ЗЕРНО };
  }
  const начало = sy * ширина + sx;
  if (!(данные[начало] >= порог)) return { маска, пикселей: 0, доКрая: false, причина: ПРИЧИНЫ.ЗЕРНО };

  const очередь = new Int32Array(N);
  let голова = 0, хвост = 0;
  очередь[хвост++] = начало;
  маска[начало] = 1;
  let пикселей = 0, доКрая = false;

  while (голова < хвост) {
    const p = очередь[голова++];
    пикселей++;
    const x = p % ширина, y = (p - x) / ширина;
    if (x === 0 || y === 0 || x === ширина - 1 || y === высота - 1) доКрая = true;
    const соседи = [
      x > 0 ? p - 1 : -1,
      x < ширина - 1 ? p + 1 : -1,
      y > 0 ? p - ширина : -1,
      y < высота - 1 ? p + ширина : -1,
    ];
    for (const q of соседи) {
      if (q < 0 || маска[q]) continue;
      if (!(данные[q] >= порог)) continue;
      if (зона && !зона[q]) {
        // Область хочет продолжиться ЗА пределы разбираемой зоны. Это ровно то
        // же, что упереться в край кадра: полость не замкнута, и обрезать её
        // границей зоны — значит выдать случайное число за площадь. Замер на
        // фантоме с разрывом стенки: без этой проверки площадь выходила
        // 1744 мм² вместо 992 — заливка уходила по разрыву в предсердие и
        // останавливалась только рамкой зоны.
        доКрая = true;
        continue;
      }
      маска[q] = 1;
      очередь[хвост++] = q;
    }
  }
  return { маска, пикселей, доКрая, причина: null };
}

/**
 * Заполнить дырки внутри маски — это и есть папиллярные мышцы и трабекулы.
 * Дырка определяется честно: заливкой ФОНА от края кадра. Всё, что фон не
 * достал и что не в маске, — внутренняя дырка.
 *
 * @returns {{добавлено: number}} сколько пикселей дописано в маску
 */
export function заполнитьДырки(маска, ширина, высота) {
  const N = ширина * высота;
  const фон = new Uint8Array(N);
  const очередь = new Int32Array(N);
  let голова = 0, хвост = 0;
  const пуск = (p) => { if (!фон[p] && !маска[p]) { фон[p] = 1; очередь[хвост++] = p; } };
  for (let x = 0; x < ширина; x++) { пуск(x); пуск((высота - 1) * ширина + x); }
  for (let y = 0; y < высота; y++) { пуск(y * ширина); пуск(y * ширина + ширина - 1); }
  while (голова < хвост) {
    const p = очередь[голова++];
    const x = p % ширина, y = (p - x) / ширина;
    if (x > 0) пуск(p - 1);
    if (x < ширина - 1) пуск(p + 1);
    if (y > 0) пуск(p - ширина);
    if (y < высота - 1) пуск(p + ширина);
  }
  let добавлено = 0;
  for (let p = 0; p < N; p++) {
    if (!маска[p] && !фон[p]) { маска[p] = 1; добавлено++; }
  }
  return { добавлено };
}

/**
 * Маска полости на ОДНОМ срезе.
 *
 * @param {{данные: ArrayLike<number>, ширина: number, высота: number,
 *          spacing: [number, number]}} срез — spacing в мм на пиксель
 * @param {[number, number]} зерно — пиксель внутри полости
 * @param {object} [нстр]
 * @returns {{ок: boolean, причина?: string, маска?: Uint8Array, площадьММ2?: number,
 *            пикселей?: number, папиллярныхММ2?: number, порог?: number, разделимость?: number}}
 */
export function маскаСреза(срез, зерно, нстр = {}) {
  const н = { ...ПО_УМОЛЧАНИЮ, ...нстр };
  const { данные, ширина, высота } = срез || {};
  const spacing = срез?.spacing || [1, 1];
  if (!данные?.length || !ширина || !высота || данные.length !== ширина * высота) {
    return { ок: false, причина: 'срез без данных или размеры не сходятся' };
  }
  const пиксельММ2 = spacing[0] * spacing[1];

  // Область интереса вокруг зерна: гистограмма по всему кадру увела бы порог
  // в сторону грудной стенки и лёгких, которых в задаче нет.
  const радиусПикс = [Math.max(3, Math.round(н.радиусЗоныММ / spacing[0])), Math.max(3, Math.round(н.радиусЗоныММ / spacing[1]))];
  const зона = new Uint8Array(ширина * высота);
  const вЗоне = [];
  for (let y = Math.max(0, зерно[1] - радиусПикс[1]); y <= Math.min(высота - 1, зерно[1] + радиусПикс[1]); y++) {
    for (let x = Math.max(0, зерно[0] - радиусПикс[0]); x <= Math.min(ширина - 1, зерно[0] + радиусПикс[0]); x++) {
      const p = y * ширина + x;
      зона[p] = 1;
      вЗоне.push(данные[p]);
    }
  }

  const первый = порогОцу(вЗоне);
  if (!Number.isFinite(первый.порог)) {
    return { ок: false, причина: ПРИЧИНЫ.РАЗДЕЛЕНИЕ, разделимость: 0 };
  }

  // ДВА КАНДИДАТА, и выбор ПО ИСХОДУ, а не по вере в гистограмму.
  //
  // Один порог Оцу на область интереса разделяет не то, что нужно: в зоне живут
  // ТРИ яркости — воздух/фон, миокард и кровь, — и порог садится между фоном и
  // миокардом. Замер на фантоме: порог 300 при яркости миокарда 300, заливка
  // уходила через стенку в правый желудочек и жир, площадь выходила вдвое
  // больше правильной (949 пикселей против 452).
  //
  // Поэтому считается ВТОРОЙ порог — Оцу по одним лишь пикселям выше первого,
  // то есть по тканям без фона: он и разделяет миокард с кровью. Кандидаты
  // пробуются от высокого к низкому, и принимается ПЕРВЫЙ, который даёт
  // замкнутую область с зерном внутри: так правило работает и когда фона в зоне
  // нет вовсе (зона целиком внутри тела) — тогда годится первый порог.
  const выше = вЗоне.filter((v) => v >= первый.порог);
  const второй = выше.length > 20 ? порогОцу(выше) : { порог: NaN, разделимость: 0 };
  const кандидаты = [];
  if (Number.isFinite(второй.порог)) кандидаты.push({ ...второй, откуда: 'второй проход (миокард/кровь)' });
  кандидаты.push({ ...первый, откуда: 'первый проход (фон/ткань)' });

  let последняяПричина = ПРИЧИНЫ.РАЗДЕЛЕНИЕ, последняя = {};
  for (const к of кандидаты) {
    if (!(к.разделимость >= н.минРазделимость)) {
      последняяПричина = ПРИЧИНЫ.РАЗДЕЛЕНИЕ; последняя = к; continue;
    }
    const з = заливкаОтЗерна(данные, ширина, высота, зерно, к.порог, зона);
    if (з.причина) { последняяПричина = з.причина; последняя = к; continue; }
    if (з.доКрая) { последняяПричина = ПРИЧИНЫ.ГРАНИЦА; последняя = к; continue; }

    let папиллярных = 0;
    if (н.заполнятьПапиллярные) папиллярных = заполнитьДырки(з.маска, ширина, высота).добавлено;
    const пикселей = з.пикселей + папиллярных;
    const площадьММ2 = пикселей * пиксельММ2;
    if (пикселей > ширина * высота * н.максДоляКадра) {
      последняяПричина = ПРИЧИНЫ.МНОГО; последняя = { ...к, площадьММ2 }; continue;
    }
    if (площадьММ2 < н.минПлощадьММ2) {
      последняяПричина = ПРИЧИНЫ.МАЛО; последняя = { ...к, площадьММ2 }; continue;
    }
    return {
      ок: true, маска: з.маска, площадьММ2, пикселей,
      папиллярныхММ2: папиллярных * пиксельММ2,
      порог: к.порог, разделимость: к.разделимость, откуда: к.откуда,
    };
  }

  return {
    ок: false, причина: последняяПричина,
    порог: последняя.порог, разделимость: последняя.разделимость,
    ...(Number.isFinite(последняя.площадьММ2) ? { площадьММ2: последняя.площадьММ2 } : {}),
  };
}

/**
 * Объём полости по стопке срезов — метод дисков.
 *
 * @param {Array<{данные, ширина, высота, spacing, толщина?: number}>} срезы
 * @param {Array<[number, number]>} зёрна — по зерну на срез
 * @param {{шагСрезаММ: number}} нстр — расстояние между центрами срезов
 * @returns {{ок: boolean, причина: string|null, объёмМЛ: number, срезов: number,
 *            отказов: number, отказыПоПричинам: Record<string, number>,
 *            папиллярныхМЛ: number, площади: number[]}}
 */
export function объёмПолости(срезы, зёрна, нстр = {}) {
  const шаг = Number(нстр.шагСрезаММ);
  if (!Array.isArray(срезы) || !срезы.length) {
    return { ок: false, причина: 'срезов не дано', объёмМЛ: NaN, срезов: 0, отказов: 0, отказыПоПричинам: {}, папиллярныхМЛ: 0, площади: [] };
  }
  if (!Number.isFinite(шаг) || шаг <= 0) {
    return { ок: false, причина: 'не задан шаг между срезами — объём без него не считается', объёмМЛ: NaN, срезов: 0, отказов: 0, отказыПоПричинам: {}, папиллярныхМЛ: 0, площади: [] };
  }

  let сумма = 0, папиллярных = 0, посчитано = 0, отказов = 0;
  const отказыПоПричинам = {};
  const площади = [];
  for (let i = 0; i < срезы.length; i++) {
    const и = маскаСреза(срезы[i], зёрна?.[i] || зёрна?.[0], нстр);
    if (!и.ок) {
      отказов++;
      const ключ = String(и.причина || 'без причины');
      отказыПоПричинам[ключ] = (отказыПоПричинам[ключ] || 0) + 1;
      площади.push(NaN);
      continue;
    }
    сумма += и.площадьММ2 * шаг;
    папиллярных += и.папиллярныхММ2 * шаг;
    площади.push(и.площадьММ2);
    посчитано++;
  }

  if (!посчитано) {
    return { ок: false, причина: 'ни один срез не дал полости', объёмМЛ: NaN, срезов: 0, отказов, отказыПоПричинам, папиллярныхМЛ: 0, площади };
  }
  return {
    ок: true, причина: null,
    объёмМЛ: сумма / 1000,            // мм³ → мл
    срезов: посчитано, отказов, отказыПоПричинам,
    папиллярныхМЛ: папиллярных / 1000,
    площади,
  };
}

/**
 * Фракция выброса по двум фазам.
 *
 * ФВ = (КДО − КСО)/КДО. Считается ТОЛЬКО когда обе фазы посчитаны по одному и
 * тому же набору срезов: если в диастоле сосчитано восемь срезов, а в систоле
 * шесть, объёмы несравнимы, и разность будет не сокращением, а разницей в
 * покрытии. Такое возвращается отказом, а не числом с оговоркой.
 *
 * @param {{диастола: ReturnType<объёмПолости>, систола: ReturnType<объёмПолости>}} о
 * @returns {{ок: boolean, причина?: string, фв?: number, кдоМЛ?: number, ксоМЛ?: number,
 *            уоМЛ?: number, срезов?: number, папиллярныеВлияниеПроцентов?: number}}
 */
export function фракцияВыброса({ диастола, систола } = {}) {
  if (!диастола?.ок || !систола?.ок) {
    return { ок: false, причина: 'одна из фаз не посчитана: ' + [диастола?.причина, систола?.причина].filter(Boolean).join('; ') };
  }
  if (диастола.срезов !== систола.срезов) {
    return { ок: false, причина: `фазы посчитаны по разному числу срезов (${диастола.срезов} и ${систола.срезов}) — объёмы несравнимы` };
  }
  const кдо = диастола.объёмМЛ, ксо = систола.объёмМЛ;
  if (!(кдо > 0) || !(ксо >= 0)) return { ок: false, причина: 'объёмы неположительны' };
  if (ксо > кдо) {
    // Систолический объём больше диастолического — фазы перепутаны либо маска
    // где-то вытекла. Отрицательную ФВ возвращать нельзя: её примут за число.
    return { ок: false, причина: `систолический объём (${ксо.toFixed(1)} мл) больше диастолического (${кдо.toFixed(1)} мл) — фазы перепутаны или маска вытекла` };
  }
  const уо = кдо - ксо;
  const фв = уо / кдо;

  // Насколько ответ зависит от папиллярных мышц: если их вклад в КДО заметен,
  // это надо сказать рядом с числом, а не в примечании к документации.
  const влияние = кдо > 0 ? (диастола.папиллярныхМЛ || 0) / кдо * 100 : 0;

  return {
    ок: true, фв, кдоМЛ: кдо, ксоМЛ: ксо, уоМЛ: уо, срезов: диастола.срезов,
    папиллярныеВлияниеПроцентов: влияние,
  };
}

/** Оговорки, которые обязаны стоять рядом с числом ФВ. */
export function оговоркиФВ(и = {}) {
  const о = [
    'Зерно внутри полости ставит человек. Автоматического поиска левого желудочка здесь нет — при зерне в правом желудочке программа посчитает его и не заметит подмены.',
    'Базальный срез и выходной отдел не определяются: считаются те срезы, которые дали. Ошибка в один базальный срез — это несколько процентов ФВ.',
    'Граница маски идёт по пикселям, а не по сглаженному контуру: на срезе 1,5 мм это ступенька в полтора миллиметра.',
    'Коррекции движения между фазами нет.',
  ];
  if (Number.isFinite(и.папиллярныеВлияниеПроцентов) && и.папиллярныеВлияниеПроцентов > 2) {
    о.unshift(`Папиллярные мышцы включены в полость и дают ${и.папиллярныеВлияниеПроцентов.toFixed(1)}% диастолического объёма — при их исключении ФВ будет другой.`);
  }
  return о;
}

export default { порогОцу, заливкаОтЗерна, заполнитьДырки, маскаСреза, объёмПолости, фракцияВыброса, оговоркиФВ, ПРИЧИНЫ };
